import { Component, ViewChildren, QueryList, OnDestroy } from '@angular/core';
import { ReminderBoxComponent } from '../../theme/components/reminder-box/reminder-box.component';
import { trigger, style, animate, transition } from '@angular/animations';
import { Reminder } from '../../shared/models/Reminder';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar, MatSlideToggleChange } from '@angular/material';
import { ReminderService } from '../../shared/services/reminder.service';
import { Subject } from 'rxjs';
import { AchievementService } from 'app/shared/services/achievement.service';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  animations: [
    trigger('flyInOutParent', [transition(':enter, :leave', [])]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: '0', height: '0', transform: 'translateY(-100px)' }),
        animate('.3s ease-out', style({ opacity: '1', height: '*', transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        style({ opacity: '1', height: '*', overflow: 'hidden', transform: 'scale(1)' }),
        animate('.3s ease-out', style({ opacity: '0', height: '0', transform: 'scale(.9)' })),
      ]),
    ]),
  ],
})
export class NotificationComponent implements OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public defaultReminder: Reminder = null;
  public myNotifications: Reminder[] = [];
  public newNotification: Reminder = new Reminder();

  private date = new Date();
  isPaused: boolean = false;
  pauseRange: {
    beginDate: { day: number; month: number; year: number };
    endDate: { day: number; month: number; year: number };
  } = {
    beginDate: { day: this.date.getDate(), month: this.date.getMonth() + 1, year: this.date.getFullYear() },
    endDate: { day: this.date.getDate(), month: this.date.getMonth() + 1, year: this.date.getFullYear() },
  };

  @ViewChildren('myReminder')
  reminderBoxes: QueryList<ReminderBoxComponent>;

  constructor(
    private reminderService: ReminderService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private achivementService: AchievementService
  ) {
    reminderService.getMyReminders().then((response) => {
      this.defaultReminder = response.defaultReminder;
      this.myNotifications = response.reminders;
    });

    reminderService.getReminderPause().then((pause) => {
      this.isPaused = pause.isPaused || false;
      this.pauseRange = pause.dateRange ? pause.dateRange : this.pauseRange;
    });
  }

  onDateRangeChanged(event: any) {
    // event equals the structure of the pauseRange object
    this.pauseRange = event;
    this.savePause();
  }

  togglePause(e: MatSlideToggleChange) {
    this.isPaused = e.checked;

    if ((this.pauseRange && this.pauseRange.beginDate && this.pauseRange.endDate) || !this.isPaused) {
      this.savePause();
      this.achivementService.updateAchievementForUser('Holiday');
    }
  }

  savePause() {
    this.reminderService
      .setReminderPause(this.isPaused, this.pauseRange)
      .then((result) => {
        this.translate
          .get('REMINDER.ALERT.savePauseSuccess')
          .takeUntil(this.ngUnsubscribe)
          .subscribe((message: string) => {
            this.snackBar.open(message, null, {
              verticalPosition: 'bottom',
              duration: 4000,
              panelClass: ['b2a-success', 'bottom'],
            });
          });
      })
      .catch((error) => {
        this.translate
          .get('REMINDER.ALERT.savePauseError')
          .takeUntil(this.ngUnsubscribe)
          .subscribe((message: string) => {
            this.snackBar.open(message, null, {
              verticalPosition: 'bottom',
              duration: 4000,
              panelClass: ['b2a-error', 'bottom'],
            });
          });
      });
  }

  addBox(sender: ReminderBoxComponent) {
    this.reminderService
      .saveNewReminder(sender.reminder)
      .then((response) => {
        this.defaultReminder = response.defaultReminder;
        this.myNotifications = response.reminders;
        this.newNotification = new Reminder();
        sender.reset();
      })
      .catch((error) => {
        this.translate
          .get('REMINDER.ALERT.addNewReminderError')
          .takeUntil(this.ngUnsubscribe)
          .subscribe((message: string) => {
            this.snackBar.open(message, null, {
              verticalPosition: 'bottom',
              duration: 4000,
              panelClass: ['b2a-error', 'bottom'],
            });
          });
      });
  }

  deleteBox(sender: ReminderBoxComponent) {
    this.reminderService
      .deleteMyReminder(sender.reminder)
      .then((response) => {
        this.leaveEditMode(sender);
        this.defaultReminder = response.defaultReminder;
        this.myNotifications = response.reminders;
      })
      .catch((error) => {
        this.translate
          .get('REMINDER.ALERT.deleteReminderError')
          .takeUntil(this.ngUnsubscribe)
          .subscribe((message: string) => {
            this.snackBar.open(message, null, {
              verticalPosition: 'bottom',
              duration: 4000,
              panelClass: ['b2a-error', 'bottom'],
            });
          });
      });
  }

  enterEditMode(sender: ReminderBoxComponent) {
    for (const box of this.reminderBoxes.toArray()) {
      if (box !== sender) {
        box.canEdit = false;
      }
    }
  }

  leaveEditMode(sender: ReminderBoxComponent) {
    this.reminderService
      .updateMyReminder(sender.reminder)
      .then((result) => {
        for (const box of this.reminderBoxes.toArray()) {
          if (box !== sender) {
            box.canEdit = true;
          }
        }
      })
      .catch((error) => {
        this.translate
          .get('REMINDER.ALERT.saveReminderError')
          .takeUntil(this.ngUnsubscribe)
          .subscribe((message: string) => {
            this.snackBar.open(message, null, {
              verticalPosition: 'bottom',
              duration: 4000,
              panelClass: ['b2a-error', 'bottom'],
            });
          });
      });
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/pages/notification/notification.component.ts